import { useMutation } from '@apollo/client';
import {
  type AffiliationRelations,
  AffiliationTypes,
  FeatureFlagTypes,
  OrganizationUserAccessTypes,
  WorkflowObjectTypes,
} from '@generated/graphql';
import type { Account, CustodianAffiliate } from '@interfaces/account';
import type { Affiliation } from '@interfaces/affiliation';
import { AccountTypes } from '@onevesthq/ov-enums';
import { EmptyState } from '@ovComponents/2-component/emptyState/emptyState';
import { CompletionBox } from '@ovComponents/3-pattern/completionBox/completionBox';
import { InnerWorkflowModal } from '@ovComponents/4-module/workflowCompletion/components/innerWorkflowModal';
import {
  CREATE_WORKFLOW_COMPLETION,
  UPDATE_WORKFLOW_COMPLETION,
} from '@ovComponents/4-module/workflowCompletion/subSteps/queries';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { ActionContext } from '@pages/ViewDetail/actionContext';
import { UserContext } from '@providers/userContextProvider';
import filterHouseholdsWithAccess from '@utils/filterHouseholdsWithAccess';
import { usePageState } from '@utils/usePageState';
import { Box, Button, Typography } from 'hephaestus/web';
import { AddTwoTone } from 'hephaestus/webIcons';
import { kebabCase } from 'lodash-es';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useLocalization } from '../../../../../../hooks/useLocalization';
import { useOrganizationFeatureFlag } from '../../../../../../hooks/useOrganizationFeatureFlag';
import { useWorkflowLaunchInput } from '../../../../../../hooks/useWorkflowLaunchInput';
import type {
  AccountConfigSection,
  FieldType,
  FieldTypeOption,
} from '../accountConfig';
import type { EditAccountSubstepOptions } from '../editAccount.visual';
import { AffiliateCompletionBox } from './affiliateCompletionBox';
import { AffiliateModal } from './affiliateModal';
import type { InitiatedAffiliate } from './affiliateModal.types';
import {
  type AffiliationUpdateData,
  UPDATE_AFFILIATIONS,
} from './affiliateMutations';

export const respAccountTypes = [
  'RESP_FAMILY',
  'RESP_FAMILY_JOINT',
  'RESP_SINGLE',
  'RESP_SINGLE_JOINT',
];

const isAffiliation = (
  aff: Affiliation | CustodianAffiliate,
): aff is Affiliation => 'id' in aff && Boolean(aff.id);

const isRelatedToAffiliate = (
  aff: Affiliation,
  targetAffiliate: Affiliation,
): boolean =>
  Boolean(aff.id && targetAffiliate.relatedAffiliations?.includes(aff.id));

const findRelatedAffiliate = (
  allAffiliates: (Affiliation | CustodianAffiliate)[] | undefined,
  affiliate: Affiliation,
  affiliateType: AffiliationTypes,
): Affiliation | null =>
  allAffiliates?.find(
    (aff): aff is Affiliation =>
      isAffiliation(aff) &&
      isRelatedToAffiliate(aff, affiliate) &&
      aff.type === affiliateType,
  ) ?? null;

export const Affiliate = ({
  type,
  account,
  affiliates = [],
  multi = false,
  showAllocation = false,
  showRelationship = false,
  fields = [],
  optionalFields = [],
  defaultRelationType,
  refetch,
  updateAccount,
  onEdit,
  allAffiliates,
  updateMode = false,
  edittable = true,
  title,
  useAccountHoldersAddress,
  error,
  errorText,
  supportText,
  options,
  actionTitle,
  canUseExistingAffiliate,
  affiliateSections,
  isWidget = false,
}: {
  type: AffiliationTypes;
  account: Partial<
    Account & { accountId: string | null; householdClientGroupId?: string }
  >;
  affiliates?: Affiliation[];
  multi?: boolean;
  showAllocation?: boolean;
  showRelationship?: boolean;
  showMissingAffiliate?: boolean;
  fields?: FieldType[];
  optionalFields?: FieldType[];
  defaultRelationType?: AffiliationRelations;
  refetch?: () => void;
  updateAccount?: (account: any) => void;
  onEdit: () => void;
  allAffiliates?: (Affiliation | CustodianAffiliate)[];
  updateMode?: boolean;
  edittable?: boolean;
  title?: string;
  useAccountHoldersAddress?: boolean;
  error?: boolean;
  errorText?: string;
  supportText?: string;
  options?: EditAccountSubstepOptions;
  actionTitle?: string;
  canUseExistingAffiliate?: boolean;
  affiliateSections: AccountConfigSection[];
  isWidget?: boolean;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['affiliationTypes', 'client']);
  const navigate = useNavigate();
  const { userContext, activeOrganization, isLimitedAccess, setActiveEntity } =
    useContext(UserContext);
  const { workflowData } = useContext(WorkflowContext);
  const { refetch: refetchWorkflow, activeWorkflows } =
    useContext(ActionContext);
  const [newActiveWorkflow, setNewActiveWorkflow] = useState<any>();
  const [workflowCompletionId] = usePageState<string | undefined>(
    undefined,
    'workflow',
  );
  const respEnhancementEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.RespAffiliationsEnhancements,
  );

  const activeParentWorkflow = activeWorkflows.find(
    (x) => x.id === workflowCompletionId,
  );
  const [workflowCompletion] = useState<any>({
    ...activeParentWorkflow,
  });
  const [newJointAffiliate, setNewJointAffiliate] = useState<{
    userId: string;
    relation: AffiliationRelations;
    type: AffiliationTypes.Joint;
  } | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [innerOpen, setInnerOpen] = useState(false);
  const [action, setAction] = useState<'create' | 'edit'>('create');
  const [activeAffiliate, setActiveAffiliate] = useState<any>(null);

  const [updateWorkflowCompletion] = useMutation(UPDATE_WORKFLOW_COMPLETION());

  const buildLaunchInput = useWorkflowLaunchInput();
  const [createWorkflowCompletion] = useMutation(CREATE_WORKFLOW_COMPLETION(), {
    onCompleted: (data: any) => {
      setNewActiveWorkflow({
        ...data.createWorkflowCompletion.workflowCompletion,
        context: workflowData,
      });
      setInnerOpen(true);
      if (workflowCompletion?.id) {
        const updatingContext = {
          ...workflowData,
          childWorkflowCompletion:
            data.createWorkflowCompletion.workflowCompletion,
        };
        updateParentWorkflowCompletion(workflowCompletion.id, updatingContext);
      }
    },
  });

  const updateParentWorkflowCompletion = (
    parentWorkflowComplationId: string,
    updatedContext: any,
  ) => {
    updateWorkflowCompletion({
      variables: {
        input: {
          workflowCompletionId: parentWorkflowComplationId,
          context: updatedContext ?? undefined,
        },
      },
    });
  };

  useEffect(() => {
    if (
      workflowCompletion &&
      workflowCompletion?.context?.childWorkflowCompletion
    ) {
      setNewActiveWorkflow({
        ...workflowCompletion?.context?.childWorkflowCompletion,
        context: workflowData,
      });
    }
  }, [workflowCompletion, workflowData]);

  const [updateAffiliations] =
    useMutation<AffiliationUpdateData>(UPDATE_AFFILIATIONS);

  const getJointHolderWorkflowId = (
    accountType: AccountTypes,
    affiliateType?: AffiliationTypes,
  ) => {
    // Return default workflow id if affiliate type is not Joint or user has limited access
    if (
      (affiliateType && affiliateType !== AffiliationTypes.Joint) ||
      isLimitedAccess
    ) {
      return 'default';
    }
    if (
      accountType === AccountTypes.CASH_JOINT &&
      options?.enableJointAccountNewJointHolder
    ) {
      return options?.jointAccountHolderWorkflow;
    }
    if (
      accountType === AccountTypes.RESP_FAMILY_JOINT &&
      options?.enableJointFamilyRESPNewJointHolder
    ) {
      return options?.jointFamilyRESPHolderWorkflow;
    }
    if (
      accountType === AccountTypes.RESP_SINGLE_JOINT &&
      options?.enableJointSingleRESPNewJointHolder
    ) {
      return options?.jointSingleRESPHolderWorkflow;
    }
    return 'default';
  };

  type AffiliateForAllocation = {
    type: AffiliationTypes;
    allocation?: number;
  };

  const calculateAllocation = (
    affiliate: AffiliateForAllocation,
    affiliateList: AffiliateForAllocation[],
    targetType: AffiliationTypes,
    allocationEnabled: boolean,
  ): number => {
    // If allocation is not enabled or this affiliate is not of the target type, keep existing allocation
    if (!allocationEnabled || affiliate.type !== targetType) {
      return affiliate.allocation ?? 0;
    }

    const sameTypeCount = affiliateList.filter(
      (aff: AffiliateForAllocation) => aff.type === affiliate.type,
    ).length;

    // Distribute 100% equally among affiliates of the same type
    return sameTypeCount > 0 ? 100 / sameTypeCount : 0;
  };

  const onInitialAffiliateCreation = (initialAffiliate: InitiatedAffiliate) => {
    const jointWorkflowId = getJointHolderWorkflowId(
      account?.type as AccountTypes,
    );
    setNewJointAffiliate({
      userId: initialAffiliate.id,
      relation: initialAffiliate.relation,
      type: AffiliationTypes.Joint,
    });
    const launch = buildLaunchInput({
      objectType: WorkflowObjectTypes.User,
      ownerId: initialAffiliate.id,
    });
    if ('error' in launch) return;
    createWorkflowCompletion({
      variables: {
        input: {
          ...launch.input,
          workflowId: jointWorkflowId,
          organizationId: activeOrganization?.id,
          parentWorkflowCompletionId:
            workflowCompletion && workflowCompletion?.id
              ? workflowCompletion?.id
              : undefined,
        },
      },
    });
  };

  const createOrEditAffiliate = (
    actionType: 'create' | 'edit',
    aff?: Affiliation,
  ) => {
    setActiveAffiliate(
      aff ?? { relation: defaultRelationType || undefined, user: {} },
    );
    const jointWorkflowId = getJointHolderWorkflowId(
      account?.type as AccountTypes,
    );
    const isJointAffiliateWithWorkflow =
      type === AffiliationTypes.Joint && jointWorkflowId !== 'default';
    if (isJointAffiliateWithWorkflow) {
      if (actionType === 'create') {
        setAction(actionType);
        setOpen(true);
      }
      if (aff && aff?.user?.id && actionType === 'edit') {
        setAction(actionType);
        // initiate update affiliate child workflow
        const launch = buildLaunchInput({
          objectType: WorkflowObjectTypes.User,
          ownerId: aff.user.id,
        });
        if ('error' in launch) return;
        createWorkflowCompletion({
          variables: {
            input: {
              ...launch.input,
              workflowId: jointWorkflowId,
              organizationId: activeOrganization?.id,
              parentWorkflowCompletionId:
                workflowCompletion && workflowCompletion?.id
                  ? workflowCompletion?.id
                  : undefined,
            },
          },
        });
      }
    } else {
      setAction(actionType);
      setOpen(true);
    }
  };

  const handleAffiliationsUpdated = ({
    updateData,
  }: {
    updateData?: AffiliationUpdateData | null;
  }) => {
    const updatedAffiliations =
      updateData?.updateAffiliations?.account?.affiliations;
    if (!updatedAffiliations || !updateAccount) {
      return;
    }
    updateAccount((prev: Partial<Account>) => ({
      ...prev,
      affiliations: updatedAffiliations,
    }));
  };

  const removeAffiliate = async (affiliateId: string) => {
    const newAff = (allAffiliates?.filter(
      (item) => 'id' in item && item.id !== affiliateId,
    ) || []) as Affiliation[];
    const allocationEnabled =
      fields.filter((f: FieldType) =>
        ['allocation', 'allocated'].includes(f.type as FieldTypeOption),
      )?.length > 0;

    const { data } = await updateAffiliations({
      variables: {
        input: {
          accountId: account.accountId,
          affiliations: newAff.map((item) => ({
            id: item.id,
            allocation: calculateAllocation(
              item,
              newAff,
              type,
              allocationEnabled,
            ),
            relation: item.relation,
            type: item.type,
            relatedAffiliations: (item.relatedAffiliations || []).filter((id) =>
              newAff.some((a) => a.id === id),
            ),
            userId: item.user.id,
          })),
        },
      },
    });
    handleAffiliationsUpdated({ updateData: data });
  };

  const updateAccountAffiliations = async () => {
    const currentAffiliates = account?.affiliations || [];
    const allocationEnabled =
      fields.filter((f: FieldType) =>
        ['allocation', 'allocated'].includes(f.type as FieldTypeOption),
      )?.length > 0;
    if (action === 'create') {
      const updatedAffliates = newJointAffiliate
        ? [...currentAffiliates, newJointAffiliate]
        : currentAffiliates;
      const { data } = await updateAffiliations({
        variables: {
          input: {
            accountId: account.accountId,
            affiliations: updatedAffliates.map((aff) => ({
              id: 'id' in aff ? aff.id : undefined,
              userId: 'userId' in aff ? aff?.userId : aff?.user?.id,
              relation: aff.relation,
              type: aff.type,
              allocation: calculateAllocation(
                aff,
                updatedAffliates,
                type,
                allocationEnabled,
              ),
              relatedAffiliations:
                'relatedAffiliations' in aff ? aff.relatedAffiliations : [],
            })),
          },
        },
      });
      handleAffiliationsUpdated({ updateData: data });
    }
    if (action === 'edit') {
      await refetch?.();
    }
  };

  const onChange = (event: any, affiliateId: string) => {
    const newAff = allAffiliates?.map((item: any) => {
      if (item.id === affiliateId) {
        return { ...item, allocation: parseFloat(event.target.value) };
      }
      return item;
    });

    onEdit();
    updateAccount?.({ ...account, affiliations: newAff });
  };

  const defaultAffiliateType =
    type === 'OTHER' && defaultRelationType ? defaultRelationType : type;

  const isRESPAccount = respAccountTypes.includes(account.type || '');
  const isPrimaryBeneficiary = type === AffiliationTypes.PrimaryBeneficiary;
  const showContributionAmount =
    options?.useCustodianAffiliates &&
    options?.hideContributionAmount === false &&
    isPrimaryBeneficiary &&
    isRESPAccount;
  const showGrantAmount =
    options?.useCustodianAffiliates &&
    options?.hideGrantAmount === false &&
    isPrimaryBeneficiary &&
    isRESPAccount;
  // Only will show Guardian and PrimaryCaregiver if they exist on the Beneficiary
  const showGuardianAndCaregiver =
    respEnhancementEnabled && isRESPAccount && isPrimaryBeneficiary;

  const processedAffiliates = useMemo(
    () =>
      affiliates.map((affiliate: Affiliation) => {
        if (!respEnhancementEnabled) {
          return { ...affiliate, guardian: null, primaryCaregiver: null };
        }

        const guardian = findRelatedAffiliate(
          allAffiliates,
          affiliate,
          AffiliationTypes.Guardian,
        );
        const primaryCaregiver = findRelatedAffiliate(
          allAffiliates,
          affiliate,
          AffiliationTypes.PrimaryCaregiver,
        );

        return { ...affiliate, guardian, primaryCaregiver };
      }),
    [affiliates, respEnhancementEnabled, allAffiliates],
  );

  const getAffiliateProfileClick = (affiliateUserId?: string) => {
    if (!isWidget || !affiliateUserId) return undefined;
    if (userContext.accessType === OrganizationUserAccessTypes.Organization) {
      return () => navigate(`/clients/${affiliateUserId}`);
    }
    const entities =
      (userContext.accessType === OrganizationUserAccessTypes.RepCode
        ? userContext.repCodeEntities
        : userContext.entities) ?? [];
    const affiliateEntity = entities.find(
      (en) => !en.entity.affiliateOnly && en.entity.id === affiliateUserId,
    )?.entity;
    if (!affiliateEntity) return undefined;
    // Household-default roles must land on the household page, which needs
    // the household switched too; not supported here, so the card stays inert.
    const isHouseholdDefaultView =
      !!userContext.role?.makeHouseholdDefaultView &&
      filterHouseholdsWithAccess(
        affiliateEntity.households ?? [],
        affiliateEntity.id,
        userContext,
      ).length > 0;
    if (isHouseholdDefaultView) return undefined;
    return () => setActiveEntity(affiliateEntity);
  };

  const affiliateSetting = useMemo(
    () =>
      account.custodianConnection?.accountTypeSettings
        ?.find((setting) => setting.type === account.type)
        ?.affiliateSettings?.find((setting) => setting.affiliateType === type),
    [account.custodianConnection?.accountTypeSettings, account.type, type],
  );

  if (affiliates.length === 0 && !edittable) {
    return (
      <Box
        sx={{
          mb: 2,
        }}
      >
        <EmptyState>
          <Typography variant='bodyLarge'>
            {t('noLinkedAffiliate', { type: title ?? t(defaultAffiliateType) })}
          </Typography>
        </EmptyState>
      </Box>
    );
  }

  return (
    <>
      {processedAffiliates.length > 0 ? (
        processedAffiliates.map((affiliate) => (
          <AffiliateCompletionBox
            key={affiliate.id ?? affiliate.user?.id ?? `${type}-affiliate`}
            testId={`affiliate-completion-box-${kebabCase(type)}`}
            updateMode={updateMode}
            affiliate={affiliate}
            title={title ?? t(defaultAffiliateType)}
            edittable={edittable}
            error={error}
            errorText={errorText}
            showRelationship={showRelationship}
            showAllocation={showAllocation}
            showGuardian={showGuardianAndCaregiver}
            showPrimaryCaregiver={showGuardianAndCaregiver}
            guardian={affiliate.guardian}
            primaryCaregiver={affiliate.primaryCaregiver}
            supportText={supportText}
            supportUrl={translateBackendString(
              userContext.organization?.translatedSupportUrl,
            )}
            createOrEditAffiliate={createOrEditAffiliate}
            removeAffiliate={removeAffiliate}
            onChange={onChange}
            onSelect={getAffiliateProfileClick(affiliate.user?.id)}
            showContributionAmount={showContributionAmount}
            showGrantAmount={showGrantAmount}
            options={options}
            accountData={account}
            refetch={refetch}
          />
        ))
      ) : (
        <CompletionBox
          testId={`affiliate-completion-box-${type}`}
          variant={updateMode ? 'view' : 'edit'}
          state='todo'
          edittable
          title={actionTitle ?? t(`button.${defaultAffiliateType}`)}
          onAdd={() => createOrEditAffiliate('create')}
          error={error}
          errorText={errorText}
        >
          <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
            {affiliateSetting?.description ||
              t(`description.${account.type}.${defaultAffiliateType}`)}
          </Typography>
        </CompletionBox>
      )}
      {multi &&
        edittable &&
        affiliates.filter((x: any) => x.type === type).length >= 1 && (
          <Button
            variant='text'
            startIcon={<AddTwoTone />}
            onClick={() => createOrEditAffiliate('create')}
            sx={{ mb: 1 }}
          >
            {t(`button.${type}`)}
          </Button>
        )}
      <AffiliateModal
        data-testid='affiliate-modal'
        open={open}
        setOpen={setOpen}
        type={type}
        accountId={account.accountId || ''}
        fields={fields}
        optionalFields={optionalFields}
        affiliate={
          activeAffiliate || {
            relation: defaultRelationType || undefined,
            user: {},
          }
        }
        allAffiliates={allAffiliates}
        affiliates={affiliates}
        refetch={refetch}
        action={action}
        useAccountHoldersAddress={useAccountHoldersAddress}
        account={account}
        title={actionTitle}
        canUseExistingAffiliate={canUseExistingAffiliate}
        options={options}
        onJointAffiliateCreation={
          getJointHolderWorkflowId(account?.type as AccountTypes, type) !==
          'default'
            ? onInitialAffiliateCreation
            : undefined
        }
        isCustomJointAffiliateFlowUsed={
          getJointHolderWorkflowId(account?.type as AccountTypes, type) !==
          'default'
        }
        affiliateSections={affiliateSections}
        isWidget={isWidget}
        onAffiliationsUpdated={handleAffiliationsUpdated}
      />

      {newActiveWorkflow && (
        <InnerWorkflowModal
          newActiveWorkflow={newActiveWorkflow}
          setOpen={setInnerOpen}
          open={innerOpen}
          refetch={() => {
            refetchWorkflow();
            updateAccountAffiliations();
          }}
          updateParent={(activeCompletion?: any) => {
            if (workflowCompletion && workflowCompletion.id) {
              if (!activeCompletion) {
                delete workflowData.childWorkflowCompletion;
                updateParentWorkflowCompletion(workflowCompletion.id, {
                  ...workflowData,
                });
              } else {
                const updatingContext = {
                  ...workflowData,
                  childWorkflowCompletion: activeCompletion,
                };
                updateParentWorkflowCompletion(
                  workflowCompletion.id,
                  updatingContext,
                );
              }
            }
          }}
        />
      )}
    </>
  );
};
